"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ViewportList = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
var react_1 = require("react");
var IS_SSR = typeof window === 'undefined';
var IS_TOUCH_DEVICE = IS_SSR ? false : function () {
  try {
    return 'ontouchstart' in window || navigator.maxTouchPoints;
  } catch (error) {
    return false;
  }
}();
var IS_OVERFLOW_ANCHOR_SUPPORTED = IS_SSR ? false : function () {
  try {
    return window.CSS.supports('overflow-anchor: auto');
  } catch (error) {
    return false;
  }
}();
var PROP_NAME_FOR_Y_AXIS = {
  top: 'top',
  bottom: 'bottom',
  clientHeight: 'clientHeight',
  scrollHeight: 'scrollHeight',
  scrollTop: 'scrollTop',
  overflowY: 'overflowY',
  height: 'height',
  minHeight: 'minHeight',
  maxHeight: 'maxHeight',
  marginTop: 'marginTop'
};
var PROP_NAME_FOR_X_AXIS = {
  top: 'left',
  bottom: 'right',
  scrollHeight: 'scrollWidth',
  clientHeight: 'clientWidth',
  scrollTop: 'scrollLeft',
  overflowY: 'overflowX',
  minHeight: 'minWidth',
  height: 'width',
  maxHeight: 'maxWidth',
  marginTop: 'marginLeft'
};
var getStyle = function getStyle(propName, size, marginTop) {
  var _a;
  if (marginTop === void 0) {
    marginTop = 0;
  }
  return _a = {
    padding: 0,
    margin: 0,
    border: 'none',
    visibility: 'hidden',
    overflowAnchor: 'none'
  }, _a[propName.minHeight] = size, _a[propName.height] = size, _a[propName.maxHeight] = size, _a[propName.marginTop] = marginTop, _a;
};
var normalizeValue = function normalizeValue(min, value, max) {
  return Math.max(Math.min(value, max), min);
};
var useIsomorphicLayoutEffect = IS_SSR ? react_1.useEffect : react_1.useLayoutEffect;
var useRecursiveAnimationFrame = function useRecursiveAnimationFrame(func) {
  var stepRef = (0, react_1.useRef)(func);
  stepRef.current = func;
  useIsomorphicLayoutEffect(function () {
    var frameId;
    var frame = function frame() {
      frameId = requestAnimationFrame(frame);
      stepRef.current();
    };
    frame();
    return function () {
      return cancelAnimationFrame(frameId);
    };
  }, []);
};
var ViewportListInner = function ViewportListInner(_a, ref) {
  var _b = _a.viewportRef,
    viewportRef = _b === void 0 ? {
      current: IS_SSR ? null : document.documentElement
    } : _b,
    _c = _a.items,
    items = _c === void 0 ? [] : _c,
    _d = _a.itemMinSize,
    itemMinSize = _d === void 0 ? 1 : _d,
    _e = _a.margin,
    margin = _e === void 0 ? 0 : _e,
    _f = _a.overscan,
    overscan = _f === void 0 ? 1 : _f,
    _g = _a.axis,
    axis = _g === void 0 ? 'y' : _g,
    _h = _a.initialIndex,
    initialIndex = _h === void 0 ? -1 : _h,
    _j = _a.initialAlignToTop,
    initialAlignToTop = _j === void 0 ? true : _j,
    _k = _a.initialOffset,
    initialOffset = _k === void 0 ? 0 : _k,
    _l = _a.fixed,
    fixed = _l === void 0 ? false : _l,
    children = _a.children,
    onViewportIndexesChange = _a.onViewportIndexesChange,
    _m = _a.overflowAnchor,
    overflowAnchor = _m === void 0 ? 'auto' : _m;
  var propName = axis === 'y' ? PROP_NAME_FOR_Y_AXIS : PROP_NAME_FOR_X_AXIS;
  var maxIndex = items.length - 1;
  var normalizedItemMinSize = Math.max(1, itemMinSize);
  var itemMinSizeWithMargin = normalizedItemMinSize + margin;
  var overscanSize = Math.ceil(Math.max(0, overscan) * itemMinSizeWithMargin);
  var _o = (0, react_1.useState)([initialIndex, initialIndex]),
    indexes = _o[0],
    setIndexes = _o[1];
  var startIndex = indexes[0] = normalizeValue(0, indexes[0], maxIndex);
  var endIndex = indexes[1] = normalizeValue(startIndex, indexes[1], maxIndex);
  var topSpacerRef = (0, react_1.useRef)(null);
  var bottomSpacerRef = (0, react_1.useRef)(null);
  var cacheRef = (0, react_1.useRef)([]);
  var scrollToIndexRef = (0, react_1.useRef)(initialIndex >= 0 ? {
    index: initialIndex,
    alignToTop: initialAlignToTop,
    offset: initialOffset
  } : null);
  var marginTopRef = (0, react_1.useRef)(0);
  var viewportIndexesRef = (0, react_1.useRef)([-1, -1]);
  var anchorIndexRef = (0, react_1.useRef)(-1);
  var topSpacerStyle = (0, react_1.useMemo)(function () {
    return getStyle(propName, cacheRef.current.slice(0, startIndex).reduce(function (sum, next) {
      return sum + (next - normalizedItemMinSize);
    }, startIndex * itemMinSizeWithMargin), marginTopRef.current);
  }, [normalizedItemMinSize, itemMinSizeWithMargin, startIndex, propName]);
  var bottomSpacerStyle = (0, react_1.useMemo)(function () {
    return getStyle(propName, cacheRef.current.slice(endIndex + 1, maxIndex + 1).reduce(function (sum, next) {
      return sum + (next - normalizedItemMinSize);
    }, itemMinSizeWithMargin * (maxIndex - endIndex)));
  }, [propName, endIndex, maxIndex, itemMinSizeWithMargin, normalizedItemMinSize]);
  useRecursiveAnimationFrame(function () {
    var viewport = viewportRef.current;
    var topSpacer = topSpacerRef.current;
    var bottomSpacer = bottomSpacerRef.current;
    if (!viewport || !topSpacer || !bottomSpacer) {
      return;
    }
    if (marginTopRef.current && (viewport[propName.scrollTop] <= 0 || startIndex === 0)) {
      topSpacer.style[propName.marginTop] = '0px';
      viewport.style[propName.overflowY] = 'hidden';
      viewport[propName.scrollTop] += -marginTopRef.current;
      viewport.style[propName.overflowY] = '';
      marginTopRef.current = 0;
      return;
    }
    var topElement = topSpacer.nextSibling;
    var bottomElement = bottomSpacer.previousSibling;
    var viewportRect = viewport.getBoundingClientRect();
    var topElementRect = topElement.getBoundingClientRect();
    var bottomElementRect = bottomElement.getBoundingClientRect();
    var topLimit = normalizeValue(0, viewportRect[propName.top], document.documentElement[propName.clientHeight]);
    var bottomLimit = normalizeValue(0, viewportRect[propName.bottom], document.documentElement[propName.clientHeight]);
    var topLimitWithOverscanSize = topLimit - overscanSize;
    var bottomLimitWithOverscanSize = bottomLimit + overscanSize;
    var maxItemsCountInViewPort = Math.ceil((bottomLimitWithOverscanSize - topLimitWithOverscanSize) / itemMinSizeWithMargin);
    var nextStartIndex = startIndex;
    var nextEndIndex = endIndex;
    if (scrollToIndexRef.current) {
      var targetIndex = normalizeValue(0, scrollToIndexRef.current.index, maxIndex);
      if (targetIndex >= startIndex && targetIndex <= endIndex) {
        var index = startIndex;
        var element = topElement;
        while (element && element !== bottomSpacer) {
          cacheRef.current[index] = element[propName.clientHeight];
          if (index === targetIndex) {
            element.scrollIntoView(scrollToIndexRef.current.alignToTop);
            if (scrollToIndexRef.current.offset) {
              viewport[propName.scrollTop] += scrollToIndexRef.current.offset;
            }
            scrollToIndexRef.current = null;
            return;
          }
          index++;
          element = element.nextSibling;
        }
        return;
      }
      nextStartIndex = targetIndex - maxItemsCountInViewPort;
      nextEndIndex = targetIndex + maxItemsCountInViewPort;
    } else if (fixed) {
      nextStartIndex = Math.floor((topLimitWithOverscanSize - topSpacerRef.current.getBoundingClientRect()[propName.top]) / itemMinSizeWithMargin);
      nextEndIndex = nextStartIndex + maxItemsCountInViewPort;
    } else if (topElementRect[propName.top] >= bottomLimitWithOverscanSize) {
      // fast scroll up
      var diff = topElementRect[propName.top] - bottomLimitWithOverscanSize;
      nextEndIndex = startIndex;
      while (diff >= 0 && nextEndIndex > 0) {
        nextEndIndex--;
        diff -= (cacheRef.current[nextEndIndex] || normalizedItemMinSize) + margin;
      }
      nextStartIndex = nextEndIndex - maxItemsCountInViewPort;
    } else if (bottomElementRect[propName.bottom] + margin <= topLimitWithOverscanSize) {
      // fast scroll down
      var diff = topLimitWithOverscanSize - bottomElementRect[propName.bottom] - margin;
      nextStartIndex = endIndex;
      while (diff >= 0 && nextStartIndex < maxIndex) {
        nextStartIndex++;
        diff -= (cacheRef.current[nextStartIndex] || normalizedItemMinSize) + margin;
      }
      nextEndIndex = nextStartIndex + maxItemsCountInViewPort;
    } else {
      if (topElementRect[propName.top] >= topLimitWithOverscanSize) {
        // scroll up
        var diff = topElementRect[propName.top] - topLimitWithOverscanSize;
        while (diff >= 0 && nextStartIndex > 0) {
          nextStartIndex--;
          diff -= (cacheRef.current[nextStartIndex] || normalizedItemMinSize) + margin;
        }
      } else if (topElementRect[propName.bottom] + margin < topLimitWithOverscanSize) {
        // scroll down (correction)
        var diff = topLimitWithOverscanSize - topElementRect[propName.bottom] - margin;
        var element = topElement.nextSibling;
        while (diff >= 0 && element && element !== bottomSpacer) {
          nextStartIndex++;
          cacheRef.current[nextStartIndex] = element[propName.clientHeight];
          diff -= (cacheRef.current[nextStartIndex] || normalizedItemMinSize) + margin;
          element = element.nextSibling;
        }
      }
      if (bottomElementRect[propName.bottom] + margin <= bottomLimitWithOverscanSize) {
        // scroll down
        var diff = bottomLimitWithOverscanSize - bottomElementRect[propName.bottom] - margin;
        while (diff >= 0 && nextEndIndex < maxIndex) {
          nextEndIndex++;
          diff -= (cacheRef.current[nextEndIndex] || normalizedItemMinSize) + margin;
        }
      } else if (bottomElementRect[propName.top] > bottomLimitWithOverscanSize) {
        // scroll up (correction)
        var diff = bottomElementRect[propName.top] - bottomLimitWithOverscanSize;
        var element = bottomElement.previousSibling;
        while (diff >= 0 && element && element !== topSpacer) {
          nextEndIndex--;
          cacheRef.current[nextEndIndex] = element[propName.clientHeight];
          diff -= (cacheRef.current[nextEndIndex] || normalizedItemMinSize) + margin;
          element = element.previousSibling;
        }
      }
    }
    if (onViewportIndexesChange) {
      var index = startIndex;
      var element = topElement;
      var startViewportIndex = startIndex;
      while (element && element !== bottomSpacer) {
        if (element.getBoundingClientRect()[propName.bottom] > topLimit) {
          startViewportIndex = index;
          break;
        }
        index++;
        element = element.nextSibling;
      }
      index = endIndex;
      element = bottomElement;
      var endViewportIndex = endIndex;
      while (element && element !== topSpacer) {
        if (element.getBoundingClientRect()[propName.top] < bottomLimit) {
          endViewportIndex = index;
          break;
        }
        index--;
        element = element.previousSibling;
      }
      if (startViewportIndex !== viewportIndexesRef.current[0] || endViewportIndex !== viewportIndexesRef.current[1]) {
        viewportIndexesRef.current = [startViewportIndex, endViewportIndex];
        onViewportIndexesChange(viewportIndexesRef.current);
      }
    }
    nextStartIndex = normalizeValue(0, nextStartIndex, maxIndex);
    nextEndIndex = normalizeValue(nextStartIndex, nextEndIndex, maxIndex);
    if (nextStartIndex === startIndex && nextEndIndex === endIndex) {
      return;
    }
    if (nextStartIndex !== startIndex) {
      anchorIndexRef.current = Math.max(nextStartIndex, startIndex);
    }
    if (fixed) {
      cacheRef.current = [];
    } else {
      var index = startIndex;
      var element = topElement;
      while (element && index < nextStartIndex && element !== bottomSpacer) {
        cacheRef.current[index] = element[propName.clientHeight];
        index++;
        element = element.nextSibling;
      }
      index = endIndex;
      element = bottomElement;
      while (element && index > nextEndIndex && element !== topSpacer) {
        cacheRef.current[index] = element[propName.clientHeight];
        index--;
        element = element.previousSibling;
      }
    }
    setIndexes([nextStartIndex, nextEndIndex]);
  });
  useIsomorphicLayoutEffect(function () {
    var anchorIndex = anchorIndexRef.current;
    anchorIndexRef.current = -1;
    var viewport = viewportRef.current;
    var topSpacer = topSpacerRef.current;
    var bottomSpacer = bottomSpacerRef.current;
    if (IS_OVERFLOW_ANCHOR_SUPPORTED && overflowAnchor !== 'none' || anchorIndex === -1 || !viewport || !topSpacer || !bottomSpacer) {
      return;
    }
    var topElement = topSpacer.nextSibling;
    var index = startIndex;
    var element = topElement;
    var diff = 0;
    while (element && index < anchorIndex && element !== bottomSpacer) {
      diff += element[propName.clientHeight] - (cacheRef.current[index] || normalizedItemMinSize);
      cacheRef.current[index] = element[propName.clientHeight];
      index++;
      element = element.nextSibling;
    }
    if (!diff) {
      return;
    }
    if (IS_TOUCH_DEVICE) {
      marginTopRef.current -= diff;
      topSpacer.style[propName.marginTop] = marginTopRef.current + "px";
      return;
    }
    viewport[propName.scrollTop] += diff;
  }, [startIndex]);
  (0, react_1.useImperativeHandle)(ref, function () {
    return {
      scrollToIndex: function scrollToIndex(index, alignToTop, offset) {
        if (index === void 0) {
          index = -1;
        }
        if (alignToTop === void 0) {
          alignToTop = true;
        }
        if (offset === void 0) {
          offset = 0;
        }
        scrollToIndexRef.current = {
          index: index,
          alignToTop: alignToTop,
          offset: offset
        };
      }
    };
  }, []);
  return (0, jsx_runtime_1.jsxs)(react_1.Fragment, {
    children: [(0, jsx_runtime_1.jsx)("div", {
      ref: topSpacerRef,
      style: topSpacerStyle
    }, void 0), items.slice(startIndex, endIndex + 1).map(function (item, index) {
      return children(item, startIndex + index, items);
    }), (0, jsx_runtime_1.jsx)("div", {
      ref: bottomSpacerRef,
      style: bottomSpacerStyle
    }, void 0)]
  }, void 0);
};
exports.ViewportList = (0, react_1.forwardRef)(ViewportListInner);
// eslint-disable-next-line import/no-default-export
exports.default = exports.ViewportList;